/**
 * Dashboard-definition API — CRUD over /api/v1/dashboard and
 * /api/v1/dashboard-widget (the M1 composable-dashboards backend). Distinct
 * from api/dashboard.ts, which wraps the OLD fixed-aggregation reads
 * (/dashboard/portfolio, /dashboard/workload, ...) still used by builtin
 * widget Hosts (T5) — the two files' query keys never collide
 * ('dashboard-def*' vs 'portfolio-dashboard'/'dashboard-account'/...), so
 * invalidation stays independent by design: a widget CRUD/layout change
 * never needs to refetch the old aggregation reads, and vice versa.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  DashboardResponse,
  DashboardWidgetResponse,
  DashboardVisibility,
  DashboardContext,
  WidgetKind,
  BuiltinWidget,
  MetricViz,
} from '@/types/domain';

interface ApiEnvelope<T> {
  data: T;
  error: { code: string; message: string } | null;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const LIST_KEY = ['dashboard-def-list'] as const;
const detailKey = (id: string) => ['dashboard-def', id] as const;

// ---------------------------------------------------------------------------
// Payloads
// ---------------------------------------------------------------------------

export interface CreateDashboardPayload {
  name: string;
  visibility: DashboardVisibility;
  context: DashboardContext;
  accountId?: string | null;
}

export interface UpdateDashboardPayload {
  name: string;
  accountId?: string | null;
}

export interface CreateDashboardWidgetPayload {
  dashboardId: string;
  kind: WidgetKind;
  builtinRef?: BuiltinWidget | null;
  metricDefinitionId?: string | null;
  viz?: MetricViz | null;
  title?: string | null;
  config?: string | null;
  gridX: number;
  gridY: number;
  gridW: number;
  gridH: number;
}

export interface UpdateDashboardWidgetPayload {
  title?: string | null;
  config?: string | null;
  gridX: number;
  gridY: number;
  gridW: number;
  gridH: number;
}

export interface LayoutItem {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** GET /dashboard — own + shared, collection shape (widgets: null). */
export function useDashboards() {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<DashboardResponse[]>>('/dashboard');
      return res.data.data;
    },
    staleTime: 30_000,
  });
}

/** GET /dashboard/{id} — detail shape, widgets populated. */
export function useDashboard(id: string) {
  return useQuery({
    queryKey: detailKey(id),
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<DashboardResponse>>(`/dashboard/${id}`);
      return res.data.data;
    },
    enabled: !!id,
    staleTime: 10_000,
  });
}

// ---------------------------------------------------------------------------
// Dashboard mutations
// ---------------------------------------------------------------------------

export function useCreateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDashboardPayload) =>
      api.post<ApiEnvelope<DashboardResponse>>('/dashboard', payload).then((res) => res.data.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useUpdateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDashboardPayload }) =>
      api.put<ApiEnvelope<DashboardResponse>>(`/dashboard/${id}`, payload).then((res) => res.data.data),
    onSuccess: (item) => {
      void qc.invalidateQueries({ queryKey: LIST_KEY });
      void qc.invalidateQueries({ queryKey: detailKey(item.id) });
    },
  });
}

export function useDeleteDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<ApiEnvelope<void>>(`/dashboard/${id}`).then(() => id),
    onSuccess: (id) => {
      void qc.invalidateQueries({ queryKey: LIST_KEY });
      void qc.invalidateQueries({ queryKey: detailKey(id) });
    },
  });
}

export function useDuplicateDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, visibility }: { id: string; visibility?: DashboardVisibility }) =>
      api
        .post<ApiEnvelope<DashboardResponse>>(`/dashboard/${id}/duplicate`, visibility ? { visibility } : undefined)
        .then((res) => res.data.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useSetDefaultDashboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch<ApiEnvelope<void>>(`/dashboard/${id}/default`).then(() => id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

// ---------------------------------------------------------------------------
// Widget mutations
// ---------------------------------------------------------------------------

export function useCreateWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDashboardWidgetPayload) =>
      api.post<ApiEnvelope<DashboardWidgetResponse>>('/dashboard-widget', payload).then((res) => res.data.data),
    onSuccess: (item) => void qc.invalidateQueries({ queryKey: detailKey(item.dashboardId) }),
  });
}

export function useUpdateWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; dashboardId: string; payload: UpdateDashboardWidgetPayload }) =>
      api.put<ApiEnvelope<DashboardWidgetResponse>>(`/dashboard-widget/${id}`, payload).then((res) => res.data.data),
    onSuccess: (_item, { dashboardId }) => void qc.invalidateQueries({ queryKey: detailKey(dashboardId) }),
  });
}

export function useDeleteWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; dashboardId: string }) =>
      api.delete<ApiEnvelope<void>>(`/dashboard-widget/${id}`).then(() => undefined),
    onSuccess: (_void, { dashboardId }) => void qc.invalidateQueries({ queryKey: detailKey(dashboardId) }),
  });
}

/** PUT /dashboard-widget/layout — batch commit on drag/resize-end ("Done"). */
export function useUpdateWidgetLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, items }: { dashboardId: string; items: LayoutItem[] }) =>
      api.put<ApiEnvelope<void>>('/dashboard-widget/layout', { dashboardId, items }).then(() => undefined),
    onSuccess: (_void, { dashboardId }) => void qc.invalidateQueries({ queryKey: detailKey(dashboardId) }),
  });
}
